/**
 * FavoritesScreen — Liste des produits favoris de l'utilisateur
 *
 * Flow de clic sur un favori :
 *  1. Tap sur carte favori → ouvre FavoriteActionModal
 *  2. Dans le modal, user choisit :
 *     - "Voir sur le site partenaire" → ouvre le browser avec tracking
 *     - "J'ai acheté" → ouvre PurchaseConfirmModal pour ajouter au dressing
 */
import React, { useState, useCallback, useEffect } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  ActivityIndicator, RefreshControl, StatusBar, Image, Alert, Platform,
} from 'react-native';
import ThemedRefreshControl from '../components/ThemedRefreshControl';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import { getFavorites, removeFavorite, restoreFavoritesFromBackend } from '../services/FavoritesService';
import { openProductForPurchase } from '../services/PurchaseTrackingService';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useResetScrollOnFocus } from '../hooks/useResetScrollOnFocus';
import FavoriteActionModal from '../components/FavoriteActionModal';
import PurchaseConfirmModal from '../components/PurchaseConfirmModal';
import BackButton from '../components/BackButton';
// v1.12.285 — fix delete favorite : Alert.alert ne marche pas sur web (window.alert sans bouton custom)
import { showAppAlert } from '../components/AppDialog';
import { useI18n } from '../i18n';

const getColors = (dark) => ({
  bg:        dark ? '#141414' : '#FAFAFA',
  card:      dark ? '#222222' : '#FFFFFF',
  primary:   dark ? '#EEEEEE' : '#1A1A1A',
  accent:    '#E74C3C',
  text:      dark ? '#EEEEEE' : '#1A1A1A',
  textLight: dark ? '#AAAAAA' : '#666',
  border:    dark ? '#383838' : '#EEE',
});

export default function FavoritesScreen({ navigation }) {
  const { user, getValidToken } = useAuth();
  const { dark } = useTheme();
  const { t, lang } = useI18n();
  const COLORS = React.useMemo(() => getColors(dark), [dark]);
  const s = React.useMemo(() => makeStyles(COLORS), [COLORS]);
  const insets = useSafeAreaInsets();
  // Scroll en haut quand on revient sur cet écran
  const scrollRef = useResetScrollOnFocus();
  // Padding top pour éviter que le header déborde sur la status bar Android
  const topPadding = Platform.OS === 'android'
    ? Math.max(insets.top, StatusBar.currentHeight || 24)
    : Math.max(insets.top, 0);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefresh]  = useState(false);

  // State modals
  const [actionProduct,   setActionProduct]   = useState(null); // FavoriteActionModal
  const [purchaseProduct, setPurchaseProduct] = useState(null); // PurchaseConfirmModal

  const load = useCallback(async () => {
    try {
      // v1.12.914 — pass user?.id en fallback (cas où SecureStore token est
      // momentanément absent / refresh en cours après login récent)
      const list = await getFavorites(user?.id);
      setFavorites(list);
    } catch (e) {
      console.warn('[Favorites] load:', e.message);
    } finally {
      setLoading(false);
      setRefresh(false);
    }
  }, [user?.id]);

  // Recharge à chaque focus de l'écran
  useFocusEffect(useCallback(() => { load(); }, [load]));

  // Restore depuis backend au 1er mount
  useEffect(() => {
    restoreFavoritesFromBackend().then(() => load());
  }, [load]);

  const onRefresh = () => { setRefresh(true); load(); };

  const handleRemove = async (product) => {
    // v1.12.285 — showAppAlert au lieu d'Alert.alert (web-compat avec boutons custom)
    const idx = await showAppAlert(
      t('remove_from_favorites_q'),
      product.name,
      [
        { text: t('cancel'), style: 'cancel' },
        { text: t('remove_btn'), style: 'destructive' },
      ]
    );
    if (idx !== 1) return;
    // v1.13.11 — suppression optimiste : on retire tout de suite de l'UI (l'item
    // ne doit jamais "rester" même si le storage/token a un souci), puis on
    // persiste avec fallbackUid + on recharge pour réconcilier.
    setFavorites(prev => prev.filter(f => String(f?.id ?? '') !== String(product.id)));
    await removeFavorite(product.id, user?.id);
    load();
  };

  // Tap sur un favori → ouvre le modal d'action (pas de redirection directe)
  const handleOpen = (product) => {
    setActionProduct(product);
  };

  // Action 1 : "Voir sur le site partenaire" → ouvre le site partenaire (tracking).
  // v1.13.68 — FIX web : NE PAS faire d'await AVANT openProductForPurchase.
  // L'await getValidToken() cassait le "geste utilisateur" → la fenêtre pré-ouverte
  // (anti-bloqueur de pop-up, v1.13.61) était bloquée → aucune redirection depuis
  // les favoris. On appelle openProductForPurchase SYNCHRONEMENT dans le geste ;
  // il récupère lui-même un token (user.accessToken ou SecureStore) pour le tracking.
  const handleOpenSite = () => {
    const p = actionProduct;
    setActionProduct(null); // ferme le modal d'action
    if (!p) return;
    const go = () => openProductForPurchase(p, user, { screen: 'favorites' })
      .catch((e) => console.warn('[Favorites] openSite:', e?.message));
    // v1.13.69 — WEB : dans le geste (sinon pop-up bloqué). MOBILE : on LAISSE le
    // modal d'action se FERMER avant d'ouvrir le navigateur, sinon conflit
    // Modal/navigateur → écran favoris figé au retour.
    if (Platform.OS === 'web') go();
    else setTimeout(go, 320);
  };

  // Action 2 : "J'ai acheté" → ferme modal d'action + ouvre PurchaseConfirmModal
  const handleMarkPurchased = () => {
    const p = actionProduct;
    setActionProduct(null);
    if (!p) return;
    setPurchaseProduct(p);
  };

  // Callback après ajout au dressing (PurchaseConfirmModal)
  const handlePurchaseConfirmed = async (item) => {
    // Optionnel : retirer des favoris après achat (l'item est déjà dans le dressing)
    try {
      const rid = actionProduct?.id || purchaseProduct?.id;
      setFavorites(prev => prev.filter(f => String(f?.id ?? '') !== String(rid)));
      await removeFavorite(rid, user?.id);
      load();
    } catch(e) {
      // silencieux
    }
  };

  const partnerColor = (p) => {
    const m = {
      cashmere: '#D4AF37', iqueens: '#5DADE2', zalando: '#FF6900',
      asos: '#111', hm: '#E50010', amazon: '#FF9900', zara: '#000',
    };
    return m[p] || '#666';
  };

  const partnerEmoji = (p) => {
    const m = { cashmere: '🧣', iqueens: '💎', zalando: '👕', asos: '👗', hm: '👖', zara: '🛍️', amazon: '📦' };
    return m[p] || '🛒';
  };

  if (loading) {
    return (
      <SafeAreaView style={s.loading}>
        <ActivityIndicator size="large" color={COLORS.accent} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={s.container} edges={['left', 'right', 'bottom']}>
      <StatusBar barStyle="dark-content" />

      {/* Header premium crème/doré */}
      <View style={[s.headerPremium, { paddingTop: topPadding + 10 }]}>
        <BackButton onPress={() => navigation.goBack()} />
        <View style={s.headerCenterPrem}>
          <Text style={s.headerBadgePrem}>{t('favorites_caps')}</Text>
          <Text style={s.headerTitlePrem}>{t('my_favorites_subtitle')}</Text>
        </View>
        <View style={{ width: 38 }} />
      </View>

      {/* Stats banner */}
      {favorites.length > 0 && (
        <View style={s.statsBannerPrem}>
          <View style={s.statsItemPrem}>
            <Text style={s.statsNumPrem}>{favorites.length}</Text>
            <Text style={s.statsLabelPrem}>{t('articles_label')}</Text>
          </View>
          <View style={s.statsDivPrem} />
          <View style={s.statsItemPrem}>
            <Text style={s.statsNumPrem}>
              {new Set(favorites.map(p => p.partner)).size}
            </Text>
            <Text style={s.statsLabelPrem}>{t('brands_label')}</Text>
          </View>
          <View style={s.statsDivPrem} />
          <View style={s.statsItemPrem}>
            <Text style={s.statsNumPrem}>
              {favorites.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0).toFixed(0)}€
            </Text>
            <Text style={s.statsLabelPrem}>{t('value_label')}</Text>
          </View>
        </View>
      )}

      {favorites.length === 0 ? (
        <View style={s.empty}>
          <Text style={s.emptyIcon}>💔</Text>
          <Text style={s.emptyTitle}>{t('no_favorites_title')}</Text>
          <Text style={s.emptySub}>
            {t('no_favorites_desc')}
          </Text>
          <TouchableOpacity style={s.emptyBtn} onPress={() => {
            // Cet écran Favoris peut être ouvert :
            //  - depuis le tab "Favoris" (navigation = TabNavigator) → navigate('Catalogue') marche
            //  - depuis Profil via Stack.push (navigation = Stack root) → il faut passer par 'Main'
            //    qui est le TabNavigator (cf. App.js ligne 264).
            // On essaie les 3 stratégies dans l'ordre du plus probable.
            try {
              // Stratégie 1 : navigate vers le TabNavigator parent + tab Catalogue
              navigation.navigate('Main', { screen: 'Catalogue' });
              return;
            } catch (e1) {
              console.warn('[Favorites] nav Main->Catalogue failed:', e1.message);
            }
            try {
              // Stratégie 2 : tab voisin (si on est déjà dans TabNavigator)
              navigation.navigate('Catalogue');
              return;
            } catch (e2) {
              console.warn('[Favorites] nav direct Catalogue failed:', e2.message);
            }
            try {
              // Stratégie 3 : remonter via getParent
              navigation.getParent()?.navigate('Catalogue');
            } catch (e3) {
              console.warn('[Favorites] nav parent->Catalogue failed:', e3.message);
            }
          }}>
            <Text style={s.emptyBtnText}>{t('explore_catalog_btn')}</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <ScrollView
          ref={scrollRef}
          contentContainerStyle={{ padding: 12, paddingBottom: 40 }}
          refreshControl={<ThemedRefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
          <View style={s.grid}>
            {favorites.map((p) => (
              <TouchableOpacity
                key={`fav_${p.id || p.name || Math.random()}`}
                style={s.card}
                onPress={() => handleOpen(p)}
                activeOpacity={0.85}
              >
                {/* Image */}
                <View style={s.imgWrap}>
                  {p.image ? (
                    <Image
                      source={{ uri: p.image }}
                      style={s.img}
                      resizeMode="cover"
                    />
                  ) : (
                    <View style={[s.img, s.imgFallback]}>
                      <Text style={{ fontSize: 40 }}>{p.emoji || partnerEmoji(p.partner)}</Text>
                    </View>
                  )}

                  {/* Heart button */}
                  <TouchableOpacity
                    style={s.heartBtn}
                    onPress={() => handleRemove(p)}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  >
                    <Text style={s.heartIcon}>❤️</Text>
                  </TouchableOpacity>

                  {/* Partner badge */}
                  <View style={[s.partnerBadge, { backgroundColor: partnerColor(p.partner) }]}>
                    <Text style={s.partnerBadgeText}>{p.brand}</Text>
                  </View>
                </View>

                {/* Infos */}
                <View style={s.info}>
                  <Text style={s.name} numberOfLines={2}>{p.name}</Text>
                  <View style={s.priceRow}>
                    <Text style={s.price}>{p.price} {p.currency === 'EUR' ? '€' : p.currency}</Text>
                    {p.originalPrice && p.originalPrice > p.price && (
                      <Text style={s.priceOrig}>{p.originalPrice} €</Text>
                    )}
                  </View>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      )}

      {/* Modal d'action : Voir sur le site / J'ai acheté */}
      {actionProduct && (
        <FavoriteActionModal
          product={actionProduct}
          onClose={() => setActionProduct(null)}
          onOpenSite={handleOpenSite}
          onMarkPurchased={handleMarkPurchased}
        />
      )}

      {/* Modal confirmation achat (ajout au dressing) */}
      {purchaseProduct && (
        <PurchaseConfirmModal
          product={purchaseProduct}
          onClose={() => setPurchaseProduct(null)}
          onConfirmed={handlePurchaseConfirmed}
        />
      )}
    </SafeAreaView>
  );
}

const makeStyles = (COLORS) => StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.bg },
  loading: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: COLORS.bg },

  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 12, backgroundColor: COLORS.card,
    borderBottomWidth: 1, borderBottomColor: COLORS.border,
  },
  backBtn: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  backTxt: { fontSize: 32, color: COLORS.text, fontWeight: '300' },
  headerCenter: { alignItems: 'center' },
  headerTitle: { fontSize: 18, fontWeight: '600', color: COLORS.text },
  headerCount: { fontSize: 12, color: COLORS.textLight, marginTop: 2 },

  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 },
  emptyIcon: { fontSize: 64, marginBottom: 16 },
  emptyTitle: { fontSize: 18, fontWeight: '700', color: COLORS.text, marginBottom: 8 },
  emptySub: { fontSize: 14, color: COLORS.textLight, textAlign: 'center', marginBottom: 24, lineHeight: 20 },
  emptyBtn: {
    backgroundColor: COLORS.accent, paddingHorizontal: 28, paddingVertical: 12,
    borderRadius: 24,
  },
  emptyBtnText: { color: '#FFF', fontWeight: '600', fontSize: 14 },

  grid: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between',
  },
  card: {
    width: '48%', backgroundColor: COLORS.card, borderRadius: 12, overflow: 'hidden',
    marginBottom: 12, borderWidth: 1, borderColor: COLORS.border,
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2,
  },
  imgWrap: { position: 'relative', width: '100%', aspectRatio: 0.85 },
  img: { width: '100%', height: '100%' },
  imgFallback: { backgroundColor: '#F0F0F0', alignItems: 'center', justifyContent: 'center' },

  heartBtn: {
    position: 'absolute', top: 8, right: 8, width: 36, height: 36, borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.95)', alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.15, shadowRadius: 3, elevation: 3,
  },
  heartIcon: { fontSize: 18 },

  partnerBadge: {
    position: 'absolute', bottom: 8, left: 8,
    paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10,
  },
  partnerBadgeText: { color: '#FFF', fontSize: 9, fontWeight: '700', letterSpacing: 0.5 },

  info: { padding: 10 },
  name: { fontSize: 12, fontWeight: '500', color: COLORS.text, lineHeight: 16, minHeight: 32 },
  priceRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6 },
  price: { fontSize: 14, fontWeight: '700', color: COLORS.text },
  priceOrig: { fontSize: 11, color: COLORS.textLight, textDecorationLine: 'line-through' },

  // ═══ Style premium : header + stats banner ═══
  headerPremium: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 14, backgroundColor: COLORS.bg,
    borderBottomWidth: 0.5, borderBottomColor: COLORS.border,
  },
  backBtnPrem: {
    width: 38, height: 38,
    borderRadius: 19,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#F5F4F0',
    borderWidth: 0.5,
    borderColor: COLORS.border,
  },
  backTxtPrem: {
    fontSize: 28,
    color: COLORS.text,
    fontWeight: '300',
    marginLeft: -2,
    marginTop: -2,
  },
  headerCenterPrem: { alignItems: 'center' },
  headerBadgePrem: {
    fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700',
    marginBottom: 4,
  },
  headerTitlePrem: {
    fontSize: 18, fontWeight: '300', color: COLORS.text, letterSpacing: 0.3,
  },

  statsBannerPrem: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: COLORS.card,
    paddingVertical: 14, paddingHorizontal: 20,
    borderBottomWidth: 0.5, borderBottomColor: COLORS.border,
  },
  statsItemPrem: { flex: 1, alignItems: 'center' },
  statsNumPrem: { fontSize: 18, fontWeight: '600', color: COLORS.text },
  statsLabelPrem: {
    fontSize: 8, letterSpacing: 1.5, color: '#999',
    textTransform: 'uppercase', marginTop: 2,
  },
  statsDivPrem: { width: 0.5, height: 28, backgroundColor: '#E0E0E0' },
});
